// XLSX
import * as XLSX from "xlsx-js-style";
// INTERFACE
import {
  OffsetsInterface,
  DefinitionByteInterface,
  SpecialSwitchInterface,
  SelectedItemsOffsetsTableInterface,
} from "../interfaces/Configurations.interface";

/**
 * Formats a date as `Month D, YYYY` using English month names.
 */
export const formatDate = (date: any) => {
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const newDate = new Date(date);
  const year = newDate.getFullYear();
  const month = newDate.getMonth();
  const day = newDate.getDay();
  const hour = newDate.getHours();
  const minute = newDate.getMinutes();
  const second = newDate.getSeconds();
  return `${monthNames[month]} ${day} - ${year} - ${hour}:${minute}:${second}`;
};

/**
 * Picks the delete-button style for the current theme, dimming it when disabled.
 */
export const styleDeleteButton = (disableFlag: boolean, theme: string) => {
  if (disableFlag) {
    if (theme === "dark" || theme === "blue") {
      return "buttonStyle__inactive__1__dark generalStyles__mrFix";
    }
    return "buttonStyle__inactive__1 generalStyles__mrFix";
  }
  return "buttonStyle__2 generalStyles__mrFix";
};

/**
 * Total byte count covered by the offsets of one offset type.
 */
export const getOffsetsCountByConfiguration = (offsetType: string, offsets: OffsetsInterface[]) => {
  let totalBytes = 0;
  if (offsetType && offsets) {
    offsets.map((item: OffsetsInterface) => {
      if (
        item &&
        item.offset_type &&
        item.offset_type.name &&
        item.offset_type.name === offsetType
      ) {
        totalBytes = item.byte_count;
      }
      return true;
    });
  }
  return totalBytes;
};

/**
 * The register type the offsets of one offset type use, or `UNDEFINED` when none is set.
 */
export const getRegisterTypeConfiguration = (offsetType: string, offsets: OffsetsInterface[]) => {
  let registerType = "UNDEFINED";
  if (offsetType && offsets) {
    offsets.map((item: OffsetsInterface) => {
      if (
        item &&
        item.offset_type &&
        item.offset_type.name &&
        item.offset_type.name === offsetType
      ) {
        registerType = item.register_type;
      }
      return true;
    });
  }
  return registerType;
};

/**
 * Finds an offset's id by its name. Returns nothing when no offset matches.
 */
export const getOffsetIdByName = (offsetType: string, offsets: OffsetsInterface[]) => {
  if (offsets) {
    for (let x = 0; x < offsets.length; x += 1) {
      if (
        offsets &&
        offsets[x] &&
        offsets[x].offset_type &&
        offsets[x].offset_type.name &&
        offsets[x].offset_type.name === offsetType
      ) {
        return offsets[x].offset_type.id;
      }
    }
  }
  return "";
};

/**
 * Finds an offset's id by the car name it is bound to.
 */
export const getOffsetIdByCarName = (carName: string, offsets: OffsetsInterface[]) => {
  if (offsets) {
    for (let x = 0; x < offsets.length; x += 1) {
      if (offsets[x].name === carName) {
        return offsets[x].id;
      }
    }
  }
  return "";
};

/**
 * Collects the attributes declared across the offsets of one offset type.
 */
export const getOffsetsAttributesByConfiguration = (
  offsetType: string,
  offsets: OffsetsInterface[],
) => {
  let attributes = {};
  if (offsetType && offsets) {
    offsets.map((item: OffsetsInterface) => {
      if (
        item &&
        item.offset_type &&
        item.offset_type.name &&
        item.offset_type.attributes &&
        item.offset_type.name === offsetType
      ) {
        attributes = item.offset_type.attributes;
      }
      return true;
    });
  }
  return attributes;
};

/**
 * The definitions belonging to one offset type, flattened for table rendering.
 */
export const getDefinitionsArrayByOffsetType = (
  offsetType: string,
  offsets: OffsetsInterface[],
) => {
  let definitions: any[] = [];
  if (offsetType && offsets) {
    offsets.map((item: OffsetsInterface) => {
      if (
        item &&
        item.offset_type &&
        item.offset_type.name &&
        item.offset_type.name === offsetType &&
        item.definitions &&
        item.definitions.length > 0
      ) {
        definitions = item.definitions;
      }
      return true;
    });
  }
  return definitions;
};

/**
 * Builds the selected-items rows shown beside the offsets table.
 */
export const buildSelectedItemsOffsetsTable = (offsets: OffsetsInterface[]) => {
  const selectedItems: SelectedItemsOffsetsTableInterface[] = [];
  if (offsets && offsets.length > 0) {
    offsets.map((item: OffsetsInterface) => {
      if (item && item.offset_type && item.offset_type.name) {
        selectedItems.push({
          key: item.offset_type.name,
          name: item.offset_type.name,
        });
      }
      return true;
    });
  }
  const uniqueSelectedItems = selectedItems.filter(
    (obj: SelectedItemsOffsetsTableInterface, index: number) =>
      index === selectedItems.findIndex((o: any) => obj.name === o.name),
  );
  return uniqueSelectedItems;
};

/**
 * Builds the column and row structure the offsets table renders from.
 */
export const buildDataOffsetsTable = (offsets: OffsetsInterface[]) => {
  const dataOffsetsTable: any = [];
  // Create columns
  if (offsets && offsets.length > 0) {
    offsets.map((item: OffsetsInterface) =>
      dataOffsetsTable.push({
        description: "",
        // liftName: item.name,
        liftName: `Car ${item.position}`,
        liftnumber: item.position,
        statusArr: [],
      }),
    );
  }
  const uniqueSelectedItems = dataOffsetsTable.filter(
    (obj: any, index: number) =>
      index === dataOffsetsTable.findIndex((o: any) => obj.liftName === o.liftName),
  );
  // Build offsets logic and distribution
  if (uniqueSelectedItems.length > 0) {
    for (let x = 0; x < uniqueSelectedItems.length; x += 1) {
      offsets.map((item: any) => {
        if (item && item.offset_type && item.offset_type.name) {
          if (uniqueSelectedItems[x].liftName === `Car ${item.position}`) {
            for (let y = 0; y < item.byte_count; y += 1) {
              uniqueSelectedItems[x].statusArr.push({
                key: `${item.offset_type.name}-${y}`,
                name: `${item.offset_type.name}-${y + 1}`,
                offset: item.initial_value,
                keyIndex: y,
                byteCount: item.byte_count,
              });
            }
          }
        }
        return true;
      });
    }
  }
  return uniqueSelectedItems;
};

/**
 * Merges special-switch entries into a definition byte list so both render together.
 */
export const addSpecialSwitchesToDefinitions = (
  definitionBytes: DefinitionByteInterface[],
  specialSwitches: SpecialSwitchInterface[],
) => {
  const newDefinitionBytes = [...definitionBytes];
  for (let index = 0; index < specialSwitches.length; index += 1) {
    if (specialSwitches[index].status === "Valid") {
      newDefinitionBytes[specialSwitches[index].byte - 1].bits[
        specialSwitches[index].bit
      ].specialSwitchName = specialSwitches[index].specialSwitchName;
      newDefinitionBytes[specialSwitches[index].byte - 1].bits[
        specialSwitches[index].bit
      ].specialSwitchId = specialSwitches[index].id;
    }
  }
  return newDefinitionBytes;
};

/**
 * Reports whether a pasted string parses as Excel data of the expected byte count.
 */
export const validateIfStringIsExcelData = (excelString: string, byteCount: number) => {
  //
  let excelData = [];
  let isExcelData = false;
  if (excelString) {
    excelData = excelString.toString().split("\n");
  }
  if (excelData.length > byteCount) {
    isExcelData = true;
  }
  return isExcelData;
};

/**
 * The CSS class for an offsets-table row, alternating by byte and theme.
 */
export const getTableRowOffsetClass = (index: number, theme: any, byte: number) => {
  if (byte % 2) {
    if (theme === "dark") {
      return "table-row-dark";
    }
    return "table-row-light";
  }
  return "";
};

/**
 * Background colour for the guided tour popover in the given theme.
 */
export const getTourBackground = (theme: string) => {
  if (theme === "light") {
    return "#f0f2f5";
  }
  if (theme === "dark") {
    return "#2a2a2a";
  }
  return "#16314d";
};

/**
 * Font colour for the guided tour popover in the given theme.
 */
export const getTourFontColor = (theme: string) => {
  if (theme === "light") {
    return "black";
  }
  if (theme === "dark") {
    return "white";
  }
  return "white";
};

/**
 * The CSS class for a table row, alternating by index and theme.
 */
export const getTableRowClass = (index: number, theme: any) => {
  if (index % 2 !== 0) {
    return `table-row-${theme}`;
  }
  return "";
};

/**
 * Whether a bank should be shown, given the current pinned-bank selection. With no pins, every bank shows.
 */
export const checkBankIsPinned = (item: any, pinedBanks: any) => {
  let displayBank = true;
  if (pinedBanks.length > 0) {
    displayBank = false;
    pinedBanks.map((item2: any) => {
      if (item2 === item.id) {
        displayBank = true;
      }
      return true;
    });
  }
  return displayBank;
};

/**
 * Builds an Excel workbook from report rows and triggers a browser download.
 */
export const generateExcelAndDownload = (data: any, reportName: string) => {
  // add 1 row to generate headers
  data.splice(0, 0, data[0]);
  let csv = "";
  // Loop the array of objects
  for (let row = 0; row < data.length; row += 1) {
    const keysAmount = Object.keys(data[row]).length;
    let keysCounter = 0;
    // If this is the first row, generate the headings
    if (row === 0) {
      // Loop each property of the object
      // eslint-disable-next-line no-restricted-syntax
      for (const key in data[row]) {
        if (key) {
          // This is to not add a comma at the last cell
          // The '\r\n' adds a new line
          csv += key + (keysCounter + 1 < keysAmount ? "," : "\r\n");
          keysCounter += 1;
        }
      }
    } else {
      // eslint-disable-next-line no-restricted-syntax
      for (const key in data[row]) {
        if (key) {
          csv += data[row][key] + (keysCounter + 1 < keysAmount ? "," : "\r\n");
          keysCounter += 1;
        }
      }
    }
    keysCounter = 0;
  }
  // Once we are done looping, download the .csv by creating a link
  const link = document.createElement("a");
  const selectedId = reportName;
  link.id = selectedId;
  link.setAttribute("href", `data:text/plain;charset=utf-8,${encodeURIComponent(csv)}`);
  link.setAttribute("download", `${reportName}.csv`);
  document.body.appendChild(link);
  const test123 = document.querySelector<HTMLElement>(`#${selectedId}`);
  if (test123) {
    test123.click();
  }
  data.splice(0, 1);
  csv = "";
};

/**
 * Excel export for the alternate definitions layout.
 */
export const generateExcelAndDownloadDefinitions2 = (data: any, reportName: string) => {
  // add 1 row to generate headers
  data.splice(0, 0, data[0]);
  let csv = "";
  // Loop the array of objects
  for (let row = 0; row < data.length; row += 1) {
    const keysAmount = Object.keys(data[row]).length;
    let keysCounter = 0;
    // If this is the first row, generate the headings
    if (row === 0) {
      // Loop each property of the object
      // eslint-disable-next-line no-restricted-syntax
      for (const key in data[row]) {
        if (key) {
          // This is to not add a comma at the last cell
          // The '\r\n' adds a new line
          csv += key + (keysCounter + 1 < keysAmount ? "," : "\r\n");
          keysCounter += 1;
        }
      }
    } else {
      // eslint-disable-next-line no-restricted-syntax
      for (const key in data[row]) {
        if (key) {
          csv += data[row][key] + (keysCounter + 1 < keysAmount ? "," : "\r\n");
          keysCounter += 1;
        }
      }
    }
    keysCounter = 0;
  }
  // Once we are done looping, download the .csv by creating a link
  const link = document.createElement("a");
  const selectedId = reportName;
  link.id = selectedId;
  link.setAttribute("href", `data:text/plain;charset=utf-8,${encodeURIComponent(csv)}`);
  link.setAttribute("download", `${reportName}.csv`);
  document.body.appendChild(link);
  const test123 = document.querySelector<HTMLElement>(`#${selectedId}`);
  if (test123) {
    test123.click();
  }
  data.splice(0, 1);
  csv = "";
};

/**
 * Excel export for definitions. Deep-copies the input first, so the caller's array is not mutated.
 */
export const generateExcelAndDownloadDefinitions = (data: any[], reportName: string) => {
  // Create a deep copy of the data to avoid mutating the original array
  const dataCopy = JSON.parse(JSON.stringify(data));
  // Create a new workbook and worksheet
  const workbook = XLSX.utils.book_new();
  const worksheetData = [];
  // Add headers to the worksheet
  const headers = Object.keys(dataCopy[0]);
  worksheetData.push(headers);
  // Add data rows to the worksheet, applying a background color to cells where isClone = true
  dataCopy.forEach((row: any) => {
    const rowData: any = [];
    headers.forEach((header) => {
      rowData.push(row[header]);
    });
    worksheetData.push(rowData);
  });
  // Convert the array into a worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
  // Add styling for cells where `isClone = true`
  dataCopy.forEach((row: any, rowIndex: any) => {
    if (row.isClone === true) {
      const excelRowIndex = rowIndex + 2; // +2 because of the headers and 1-based indexing
      headers.forEach((_, colIndex) => {
        const cellAddress = XLSX.utils.encode_cell({
          r: excelRowIndex - 1,
          c: colIndex,
        });
        if (!worksheet[cellAddress]) return;
        // Add background color style
        worksheet[cellAddress].s = {
          fill: {
            fgColor: { rgb: "FFFF00" }, // Yellow background
          },
        };
      });
    }
  });
  // Add the worksheet to the workbook
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");
  // Write the workbook to a file
  XLSX.writeFile(workbook, `${reportName}.xlsx`);
};

/**
 * One column filter: the LiftCloud model field to match (`target`) and the
 * value to match it against. Falsy/empty values are skipped by
 * {@link buildFilterParams}.
 */
export interface FilterTarget {
  target: string;
  value: string | number | undefined | null;
}

/**
 * Builds the query parameters for a filtered list request. Single filters are ANDed; the OR group is combined with OR.
 */
export const buildFilterParams = (
  singles: FilterTarget[],
  orGroup: FilterTarget[] = [],
): string => {
  const targets: string[] = [];
  const values: string[] = [];

  const pushEntry = ({ target, value }: FilterTarget, forceAnd: boolean) => {
    if (value === undefined || value === null || value === "") return;
    const sanitized = String(value).replace(/,/g, "").trim();
    if (sanitized === "") return;
    const isFirst = targets.length === 0;
    targets.push(!isFirst && forceAnd ? `&${target}` : target);
    values.push(sanitized);
  };

  orGroup.forEach((entry) => pushEntry(entry, false));
  singles.forEach((entry) => pushEntry(entry, true));

  if (targets.length === 0) return "";

  const filterTargetParam = targets.map(encodeURIComponent).join(",");
  const filterValueParam = values.map(encodeURIComponent).join(",");
  return `&filter_target=${filterTargetParam}&filter=${filterValueParam}`;
};

/**
 * Turns the user-management filter form state into a query string.
 */
export const buildFilterString = (filters: any) => {
  const orGroup: FilterTarget[] = [
    { target: "first_name", value: filters.filterFirstName },
    { target: "middle_name", value: filters.filterMiddleName },
    { target: "last_name", value: filters.filterLastName },
  ];

  const enumFilter = (value: unknown) => (value && value !== "ALL" ? String(value) : undefined);
  // ACTIVE / INACTIVE maps to a boolean column, not the raw status string.
  const activeFilter = enumFilter(filters.filterStatus)
    ? (filters.filterStatus === "ACTIVE").toString()
    : undefined;

  const singles: FilterTarget[] = [
    { target: "name", value: filters.filterName },
    { target: "email", value: filters.filterEmail },
    { target: "ip", value: filters.filterIP },
    { target: "created_at", value: filters.filterCreatedAt },
    { target: "routable_socket_address", value: filters.filterSocketAddress },
    { target: "index", value: filters.filterIndex },
    { target: "org_id", value: filters.filterOrganizationName },
    { target: "organization_id", value: filters.filterOrganizationFullName },
    { target: "address", value: filters.filterAddress },
    { target: "country", value: filters.filterCountry },
    { target: "state", value: filters.filterState },
    { target: "city", value: filters.filterCity },
    { target: "phone", value: filters.filterPhone },
    // filterOrgType, filterAdapterType, filterLiftType, and filterChannelType
    // all map to the same "type" column on their respective models — a given
    // caller only ever supplies one of them.
    { target: "type", value: enumFilter(filters.filterOrgType) },
    { target: "type", value: enumFilter(filters.filterAdapterType) },
    { target: "type", value: enumFilter(filters.filterLiftType) },
    { target: "type", value: enumFilter(filters.filterChannelType) },
    { target: "lift_type", value: enumFilter(filters.filterLiftTypeFullName) },
    { target: "protocol_type", value: enumFilter(filters.filterProtocolType) },
    { target: "auth_type", value: enumFilter(filters.filterAuthType) },
    { target: "status", value: enumFilter(filters.filterUserStatus) },
    { target: "active", value: activeFilter },
  ];

  return buildFilterParams(singles, orGroup);
};

/**
 * Whether a string is a valid `IPv4:port` pair.
 */
export const validateIpAndPortString = (ipString: string): boolean => {
  const isValid =
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?):(6553[0-5]|655[0-2][0-9]|65[0-4][0-9][0-9]|6[0-4][0-9][0-9][0-9][0-9]|[1-5](\d){4}|[1-9](\d){0,3})$/.test(
      ipString,
    );
  return isValid;
};

/**
 * Whether a string is two `IPv4:port` pairs, as used by dual-homed channels.
 */
export const validateDoubleIpAndPortString = (value: string): boolean => {
  const parts = value.split(":");
  if (parts.length !== 4) return false;

  const first = `${parts[0]}:${parts[1]}`;
  const second = `${parts[2]}:${parts[3]}`;

  return validateIpAndPortString(first) && validateIpAndPortString(second);
};

/**
 * Whether a string is a valid `host:port` pair, allowing a hostname rather than only an IP.
 */
export const validateHostAndPort = (value: string): boolean => {
  const [host, port] = value.split(":");
  if (!host || !port) return false;

  const isValidPort =
    /^(6553[0-5]|655[0-2][0-9]|65[0-4][0-9]{2}|6[0-4][0-9]{3}|[1-5][0-9]{4}|[1-9][0-9]{0,3})$/.test(
      port,
    );

  if (!isValidPort) return false;

  const isValidIp =
    /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
      host,
    );

  const isValidHostname = /^[a-zA-Z0-9.-]+$/.test(host);

  return isValidIp || isValidHostname;
};

/**
 * Whether a string is one or two `host:port` pairs.
 */
export const validateDoubleHostAndPortString = (value: string): boolean => {
  const parts = value.split(":");

  if (parts.length === 2) {
    return validateHostAndPort(value);
  }

  if (parts.length === 4) {
    const first = `${parts[0]}:${parts[1]}`;
    const second = `${parts[2]}:${parts[3]}`;
    return validateHostAndPort(first) && validateHostAndPort(second);
  }

  return false;
};

/**
 * Whether a string is a valid IPv4 address, optionally with a CIDR suffix.
 */
export const validateIpAString = (ipString: string) => {
  const isValid = /^(?!0)(?!.*\.$)((1?\d?\d|25[0-5]|2[0-4]\d)(\.|$)){4}(\/\d+)?$/.test(ipString);
  return isValid;
};

/**
 * Clones offset-type form data and normalises it for submission.
 */
export const buildOffsetsTypeDataObject = (newData: any) => {
  const copy = structuredClone(newData);
  copy.offsets.map((offset: any, indexOffset: number) => {
    copy.offset_types.map((offsetType: any) => {
      if (offset && offsetType && offset.offset_type_id === offsetType.id) {
        copy.offsets[indexOffset].offset_type = offsetType;
      }
      return true;
    });
    return true;
  });
  return copy;
};

/**
 * Builds the full offsets table, one row per byte across every offset.
 */
export const buildOffsetsFullTableData = (data_: any, offsets: any) => {
  // Build data to create offsets table
  let newArr: any = [];
  if (data_.length > 0) {
    newArr = data_;
  } else {
    newArr = buildDataOffsetsTable(offsets);
  }
  newArr.sort((a: any, b: any) => parseFloat(a.liftnumber) - parseFloat(b.liftnumber));
  const newData: any = [];
  if (newArr && newArr.length > 0) {
    for (let x = 0; x < newArr[0].statusArr.length; x += 1) {
      const extractNumber = newArr[0].statusArr[x].name.match(/(\d+)/);
      const parcialName = newArr[0].statusArr[x].name.replace(extractNumber[0].toString(), "");
      const name = parcialName.slice(0, -1);
      const byte = extractNumber[0];
      // const myArray = newArr[0].statusArr[x].name.split("-");
      const newObj: any = {
        id: newArr[0].statusArr[x].name,
        name,
        byte,
        byteCount: newArr[0].statusArr[x].totalItems,
        offsetTypePosition: newArr[0].statusArr[x].offsetTypePosition,
      };
      for (let y = 0; y < newArr.length; y += 1) {
        newObj[`${newArr[y].liftName}${newArr[y].statusArr[x].name}`] =
          newArr[y].statusArr[x].offset;
      }
      newData.push(newObj);
    }
  }
  newData.sort(
    (a: any, b: any) => parseFloat(a.offsetTypePosition) - parseFloat(b.offsetTypePosition),
  );
  return [newArr, newData];
};

/**
 * Builds the range view of the offsets table.
 */
export const buildOffsetsRangeTableData = (newData: any) => {
  let newArr: any = [];
  let selectedItems: any = [];
  // Check if component to create is form configuration or table view
  selectedItems = buildSelectedItemsOffsetsTable(newData.offsets);
  // Build data to create offsets table
  newArr = buildDataOffsetsTable(newData.offsets);
  newArr.sort((a: any, b: any) => parseFloat(a.liftnumber) - parseFloat(b.liftnumber));
  return [selectedItems, newArr];
};

/**
 * Whether any offset of this type starts at bit position zero.
 */
export const findBitStartAtCeroPosition = (offsetType: string, offsets: OffsetsInterface[]) => {
  if (!offsetType || !offsets) return false;
  // eslint-disable-next-line no-restricted-syntax
  for (const item of offsets) {
    if (
      item?.offset_type?.name === offsetType &&
      Array.isArray(item.definitions) &&
      item.definitions.length > 0
    ) {
      const sortedDefinitions = [...item.definitions].sort((a, b) => a.bit - b.bit);
      return sortedDefinitions[0]?.bit === 0;
    }
  }

  return false;
};

/**
 * Builds the byte array a definitions editor renders, one entry per byte up to `totalBytes`.
 */
export const buildDefinitionsByteArray = (
  totalBytes: number,
  offsetTypeName: string,
  positionCero: boolean,
) => {
  const newObject = [];
  let totalBits = 7;
  if (totalBytes === 1) {
    totalBits = 255;
  }
  for (let item = 0; item < totalBytes; item += 1) {
    const subObj: DefinitionByteInterface = {
      name: `${offsetTypeName} byte ${item + 1}`,
      isValid: false,
      bits: [],
    };
    for (let index2 = 0; index2 <= totalBits; index2 += 1) {
      subObj.bits.push({
        id: `${item + 1}_${index2}`,
        byte: item + 1,
        bit: positionCero ? index2 : index2 + 1,
        shortName: "",
        longName: "",
        activePosition: false,
        priority: 0,
        specialSwitchName: "",
        specialSwitchId: "",
        alert: false,
        log: false,
        severity: "",
        confirmSeconds: 0,
        helpId: 0,
        group: 0,
        pagingScheme: 0,
        thirdPartyPosition: 0,
        value: 1,
        code: "",
        faultSpect: 0,
        idDefinition: "",
        isClone: false,
        register_type: "UNDEFINED",
      });
    }
    // Add clone item if multiple bytes
    if (totalBytes > 1) {
      for (let index2 = 0; index2 <= totalBits; index2 += 1) {
        subObj.bits.push({
          id: `c_${item + 1}_${index2}`,
          byte: item + 1,
          bit: positionCero ? index2 : index2 + 1,
          shortName: "",
          longName: "",
          activePosition: false,
          priority: 0,
          specialSwitchName: "",
          specialSwitchId: "",
          alert: false,
          log: false,
          severity: "",
          confirmSeconds: 0,
          helpId: 0,
          group: 0,
          pagingScheme: 0,
          thirdPartyPosition: 0,
          value: 1,
          code: "",
          faultSpect: 0,
          idDefinition: "",
          isClone: true,
          register_type: "UNDEFINED",
        });
        // Add clone item if multiple bytes
      }
    }
    newObject.push(subObj);
  }
  return newObject;
};

/**
 * Returns a copy of an object with its keys in alphabetical order.
 */
export const sorJsonAlphabetic = (jsonObject: any) => {
  const sortedJson: any = {};
  const keys = Object.keys(jsonObject).sort(); // Obtener las claves y ordenarlas
  // Recorrer las claves ordenadas y agregar al nuevo objeto
  // eslint-disable-next-line no-restricted-syntax
  for (const key of keys) {
    sortedJson[key] = jsonObject[key];
  }
};

/**
 * A no-op, used where a callback is required but nothing should happen.
 */
export const emptyFunctionDeclaration = () => {};

/**
 * Serialises a value as pretty-printed JSON and triggers a browser download.
 */
export const downloadJSON = (data: any, filename: string) => {
  // Convertir a string con formato
  const jsonString = JSON.stringify(data, null, 2);

  // Crear un blob con el contenido
  const blob = new Blob([jsonString], { type: "application/json" });

  // Crear URL temporal para el blob
  const url = URL.createObjectURL(blob);

  // Crear un enlace invisible para descargar
  const link = document.createElement("a");
  link.href = url;
  link.download = `${filename}.json`;

  // Simular click para iniciar la descarga
  document.body.appendChild(link);
  link.click();

  // Limpiar
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};
